import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { usePageTitle } from 'framework/PageTitle';
import useSleepConsoleApi, { WakeResult } from './sleepConsoleApi';
import { ENVIRONMENTS, WAKE_HOURS, WakeableEnvironment } from './constants';

type Status =
  | { kind: 'idle' }
  | { kind: 'waking' }
  | { kind: 'awake'; result: WakeResult; environment: WakeableEnvironment; hours: number }
  | { kind: 'refused'; message: string };

const formatUntil = (value: Nullable<string>): string => {
  if (!value) {
    return '';
  }

  const until = new Date(value);

  if (Number.isNaN(until.getTime())) {
    return '';
  }

  return until.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' });
};

/**
 * Wakes a non-production environment that the scheduler has scaled down.
 *
 * This page lives in the production frontend rather than in the environments it
 * wakes, which is the point: production stays up whether or not they do, so there is
 * always somewhere to press the button from.
 */
const SleepConsolePage = () => {
  const { setPageTitle } = usePageTitle();
  const { wake } = useSleepConsoleApi();

  useEffect(() => {
    setPageTitle('Wake a non-prod environment');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [environment, setEnvironment] = useState<Nullable<WakeableEnvironment>>(null);
  const [hours, setHours] = useState<Nullable<number>>(null);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const waking = status.kind === 'waking';
  const canWake = environment !== null && hours !== null && !waking;

  const onWake = async () => {
    if (!canWake) {
      return;
    }

    setStatus({ kind: 'waking' });

    try {
      const result = await wake(environment, hours);
      setStatus({ kind: 'awake', result, environment, hours });
    } catch (error) {
      setStatus({ kind: 'refused', message: (error as Error).message });
    }
  };

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', py: 4 }}>
      <Typography variant="h5" gutterBottom>
        Wake a non-prod environment
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Non-production shuts down on weekday evenings and stays down over the weekend. Waking one brings its services,
        database and frontends back, then puts it away again when the time is up.
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={3}>
            <FormControl>
              <FormLabel id="sleep-console-environment">Environment</FormLabel>
              <RadioGroup
                aria-labelledby="sleep-console-environment"
                value={environment ?? ''}
                onChange={event => setEnvironment(event.target.value as WakeableEnvironment)}
              >
                {ENVIRONMENTS.map(option => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    disabled={waking}
                    label={
                      <>
                        {option.label}{' '}
                        <Typography component="span" variant="body2" color="text.secondary">
                          — {option.description}
                        </Typography>
                      </>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <FormControl>
              <FormLabel id="sleep-console-hours">Keep it awake for</FormLabel>
              <RadioGroup
                row
                aria-labelledby="sleep-console-hours"
                value={hours ?? ''}
                onChange={event => setHours(Number(event.target.value))}
              >
                {WAKE_HOURS.map(option => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio />}
                    disabled={waking}
                    label={`${option}h`}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <Box>
              <Button
                variant="contained"
                onClick={onWake}
                disabled={!canWake}
                startIcon={waking ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {waking ? 'Waking…' : 'Wake it up'}
              </Button>
            </Box>

            {status.kind === 'awake' && (
              <Alert severity="success">
                {status.environment} is waking up. It answers again about ten minutes from now — the database takes the
                longest.
                {formatUntil(status.result.keepAwakeUntil) &&
                  ` It goes back to sleep at ${formatUntil(status.result.keepAwakeUntil)}.`}
              </Alert>
            )}

            {status.kind === 'refused' && <Alert severity="error">{status.message}</Alert>}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SleepConsolePage;
